/**
 * reviewPromptStore — the single source of truth for the in-app review
 * solicitation state machine. Persisted to localStorage via zustand's persist
 * middleware (name `jam-sql-review-prompt`).
 *
 * State machine:
 *   pending → armed → (clicked | dismissed)
 * `clicked`/`dismissed` are terminal — nothing ever re-arms once reached.
 *
 * Arming happens two ways, both NEUTRAL usage milestones (never sentiment):
 *   - queries: the 100th successful query execution (fires a one-time toast).
 *   - tenure : >= SESSION_THRESHOLD app sessions AND >= TENURE_DAYS since first
 *     launch (shows the card only, no toast).
 *
 * See `docs/superpowers/specs/2026-07-03-review-prompt-design.md`.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  CARD_MAX_SHOWN_SESSIONS,
  QUERY_MILESTONE,
  SESSION_THRESHOLD,
  TENURE_DAYS,
} from '../reviewPromptConstants';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';

export type ReviewPromptState = 'pending' | 'armed' | 'clicked' | 'dismissed';

export interface ReviewPromptStore {
  successfulQueryCount: number;
  appSessionCount: number;
  /** ISO timestamp of the first app session that recorded, set once. */
  firstSeenAt: string | null;
  promptState: ReviewPromptState;
  armedBy: 'queries' | 'tenure' | null;
  /** App sessions in which the armed card was actually shown. */
  cardShownSessions: number;
  /** Who ended a 'dismissed' state — the user, or the auto-retirement. */
  dismissedBy: 'user' | 'auto' | null;

  /**
   * Increment on each successful query execution. Returns true EXACTLY ONCE —
   * when this increment crosses QUERY_MILESTONE while state is 'pending'. In
   * that case the store arms itself (`promptState:'armed'`, `armedBy:'queries'`)
   * BEFORE returning (crash-safe: state is persisted before the caller shows
   * anything). The count always increments, even after terminal states — it is
   * telemetry-neutral.
   */
  recordSuccessfulQuery: () => boolean;

  /**
   * Call once per app boot (module-level once-guard at the call site).
   * Increments appSessionCount, sets firstSeenAt if null, and arms with
   * 'tenure' when state is 'pending' && appSessionCount >= SESSION_THRESHOLD &&
   * days-since-firstSeenAt >= TENURE_DAYS.
   *
   * Auto-retirement runs first: an 'armed' card already shown in
   * CARD_MAX_SHOWN_SESSIONS sessions retires (`dismissed`/`auto`) at boot —
   * before any render, so the card never flashes in its final session.
   */
  recordAppSession: () => void;

  /**
   * Call once per session when the armed card actually renders (the card's
   * once-per-session shown effect). Feeds the auto-retirement counter.
   */
  recordCardShownSession: () => void;

  /** Terminal — kills toast + card forever. */
  markClicked: () => void;
  /** Terminal — kills toast + card forever. */
  markDismissed: () => void;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const useReviewPromptStore = create<ReviewPromptStore>()(
  persist(
    (set, get) => ({
      successfulQueryCount: 0,
      appSessionCount: 0,
      firstSeenAt: null,
      promptState: 'pending',
      armedBy: null,
      cardShownSessions: 0,
      dismissedBy: null,

      recordSuccessfulQuery: () => {
        const prev = get().successfulQueryCount;
        const next = prev + 1;
        const shouldArm =
          get().promptState === 'pending' &&
          prev < QUERY_MILESTONE &&
          next >= QUERY_MILESTONE;
        set({
          successfulQueryCount: next,
          ...(shouldArm ? { promptState: 'armed' as const, armedBy: 'queries' as const } : {}),
        });
        return shouldArm;
      },

      recordAppSession: () => {
        const state = get();

        // Auto-retirement (§3d-3): being ignored CARD_MAX_SHOWN_SESSIONS
        // sessions in a row is an answer. Retire at boot — before any render
        // — so the card never flashes in the session that retires it.
        if (
          state.promptState === 'armed' &&
          state.cardShownSessions >= CARD_MAX_SHOWN_SESSIONS
        ) {
          set({
            appSessionCount: state.appSessionCount + 1,
            firstSeenAt: state.firstSeenAt ?? new Date().toISOString(),
            promptState: 'dismissed',
            dismissedBy: 'auto',
          });
          if (typeof window !== 'undefined') {
            window.electronAPI?.analytics
              ?.track('feature:review_prompt_dismiss', { auto: true })
              .catch(() => {});
          }
          return;
        }

        const nextCount = state.appSessionCount + 1;
        const firstSeenAt = state.firstSeenAt ?? new Date().toISOString();
        const daysSince = (Date.now() - new Date(firstSeenAt).getTime()) / MS_PER_DAY;
        const shouldArm =
          state.promptState === 'pending' &&
          nextCount >= SESSION_THRESHOLD &&
          daysSince >= TENURE_DAYS;
        set({
          appSessionCount: nextCount,
          firstSeenAt,
          ...(shouldArm ? { promptState: 'armed' as const, armedBy: 'tenure' as const } : {}),
        });
      },

      recordCardShownSession: () =>
        set((state) => ({ cardShownSessions: state.cardShownSessions + 1 })),

      markClicked: () => set({ promptState: 'clicked' }),
      markDismissed: () => set({ promptState: 'dismissed', dismissedBy: 'user' }),
    }),
    {
      name: 'jam-sql-review-prompt',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — this is internal prompt bookkeeping the user never sees; the drop is
      // still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      version: 1,
      partialize: (state) => ({
        successfulQueryCount: state.successfulQueryCount,
        appSessionCount: state.appSessionCount,
        firstSeenAt: state.firstSeenAt,
        promptState: state.promptState,
        armedBy: state.armedBy,
        cardShownSessions: state.cardShownSessions,
        dismissedBy: state.dismissedBy,
      }),
    },
  ),
);
